import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  RefreshCw, Mail, ShieldAlert, Database, Download, AlertTriangle, CheckCircle2,
  Clock, Zap, Ban, Users, ArrowUpRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LIST_LABELS = {
  journal: { label: 'The KGOB Journal', color: '#D4AF37' },
  tax_alerts: { label: 'Tax Alerts', color: '#B45F3E' },
  holiday_hours: { label: 'Holiday Hours', color: '#5A8FBE' },
};

function fmtAge(secs) {
  if (secs == null) return '—';
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return `${Math.floor(secs / 86400)}d`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch (e) { return iso; }
}

export default function SubscriptionsTab({ sessionToken }) {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');
  const [searchList, setSearchList] = useState('all');
  const [searchRows, setSearchRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draining, setDraining] = useState(false);
  const [exportListFilter, setExportListFilter] = useState('all');

  const headers = { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' };

  const loadAll = useCallback(async () => {
    setLoading(true);
    const h = { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' };
    try {
      const [s, r, sup] = await Promise.all([
        fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/stats`, { headers: h }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/recent?limit=50`, { headers: h }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/suppressions?limit=100`, { headers: h }).then(r => r.json()),
      ]);
      setStats(s);
      setRecent(r.rows || []);
      setSuppressions(sup.rows || []);
    } catch (e) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function runSearch(e) {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (searchStatus !== 'all') params.set('status', searchStatus);
    if (searchList !== 'all') params.set('list_id', searchList);
    const r = await fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/search?${params}`, { headers })
      .then(r => r.json());
    setSearchRows(r.rows || []);
  }

  async function drainOutbox() {
    setDraining(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/outbox/drain`, {
        method: 'POST', headers,
      }).then(r => r.json());
      toast.success(`Outbox: sent ${r.outbox?.sent || 0}, failed ${r.outbox?.failed || 0}. Bridge resync: ${r.bridge_resync?.resynced || 0}`);
      loadAll();
    } catch (e) {
      toast.error('Drain failed');
    } finally {
      setDraining(false);
    }
  }

  async function retryFailed() {
    try {
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/outbox/retry-failed`, {
        method: 'POST', headers,
      }).then(r => r.json());
      toast.success(`${r.requeued} failed events re-queued.`);
      loadAll();
    } catch (e) {
      toast.error('Retry failed');
    }
  }

  async function downloadCsv() {
    const params = new URLSearchParams();
    if (exportListFilter !== 'all') params.set('list_id', exportListFilter);
    const url = `${BACKEND_URL}/api/v1/admin/subscriptions/export.csv?${params}`;
    const resp = await fetch(url, { headers });
    if (!resp.ok) {
      toast.error('Export failed');
      return;
    }
    const blob = await resp.blob();
    const dl = document.createElement('a');
    dl.href = window.URL.createObjectURL(blob);
    dl.download = `kgob_subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(dl);
    dl.click();
    dl.remove();
    toast.success('CSV downloaded');
  }

  async function suppressEmail(email) {
    const reason = prompt(`Suppress ${email}? Reason (e.g. "manual", "abuse_report"):`, 'manual');
    if (!reason) return;
    await fetch(`${BACKEND_URL}/api/v1/admin/subscriptions/suppress`, {
      method: 'POST', headers,
      body: JSON.stringify({ email, reason }),
    });
    toast.success(`${email} suppressed.`);
    loadAll();
  }

  // ----- Health pill -----
  const outboxLag = stats?.outbox?.oldest_pending_age_seconds;
  const healthStatus = !stats?.bridge?.connected
    ? { color: 'bg-red-100 text-red-800', text: 'Bridge offline' }
    : (outboxLag && outboxLag > 600)
    ? { color: 'bg-amber-100 text-amber-800', text: `Lag ${fmtAge(outboxLag)}` }
    : (stats?.outbox?.failed > 0)
    ? { color: 'bg-amber-100 text-amber-800', text: `${stats.outbox.failed} failed` }
    : { color: 'bg-green-100 text-green-800', text: 'Healthy' };

  return (
    <div className="space-y-6" data-testid="subscriptions-tab">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#0B2240] flex items-center gap-2">
            <Mail className="w-6 h-6 text-[#D4AF37]" />
            Email Subscriptions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Universal mailing-list registry · synced to <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">app.kgob.com</code> via bridge DB + signed webhooks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`${healthStatus.color} font-semibold`}>{healthStatus.text}</Badge>
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="subs-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* TOP-LINE STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Subscribers"
          value={stats?.subscribers?.total || 0}
          sublabel={`${stats?.subscribers?.active || 0} active`}
          icon={<Users className="w-5 h-5" />}
          tone="navy"
        />
        <StatCard
          label="Suppressed"
          value={stats?.suppressed || 0}
          sublabel="bounce / complaint / manual"
          icon={<Ban className="w-5 h-5" />}
          tone="rust"
        />
        <StatCard
          label="Outbox to app.kgob.com"
          value={(stats?.outbox?.pending || 0) + (stats?.outbox?.failed || 0)}
          sublabel={`${stats?.outbox?.pending || 0} pending · ${stats?.outbox?.failed || 0} failed`}
          icon={<Zap className="w-5 h-5" />}
          tone={(stats?.outbox?.failed || 0) > 0 ? 'amber' : 'navy'}
        />
        <StatCard
          label="Bridge Unsynced"
          value={(stats?.bridge?.subscribers_unsynced || 0) + (stats?.bridge?.subscriptions_unsynced || 0)}
          sublabel={stats?.bridge?.connected ? 'Bridge connected' : 'BRIDGE OFFLINE'}
          icon={<Database className="w-5 h-5" />}
          tone={stats?.bridge?.connected ? 'navy' : 'red'}
        />
      </div>

      {/* PER-LIST BREAKDOWN */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-base font-bold text-[#0B2240] mb-4">Per-List Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(stats?.per_list || []).map(item => (
              <div
                key={item.list_id}
                className="border border-gray-200 rounded-md p-4 hover:border-[#D4AF37] transition-colors"
                data-testid={`list-stats-${item.list_id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-[#0B2240]">{item.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">{item.list_id}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">{item.active}</div>
                    <div className="text-[10px] uppercase text-gray-500">active</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">{item.pending}</div>
                    <div className="text-[10px] uppercase text-gray-500">pending</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-400">{item.unsubscribed}</div>
                    <div className="text-[10px] uppercase text-gray-500">opted out</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* OPS ACTIONS */}
      <Card className="bg-[#FAF8F2] border-[#E1DFD7]">
        <CardContent className="p-6">
          <h3 className="text-base font-bold text-[#0B2240] mb-3 flex items-center gap-2">
            <Database className="w-4 h-4 text-[#D4AF37]" />
            Bridge &amp; Outbox Operations
          </h3>
          <p className="text-xs text-gray-600 mb-4">
            All subscriber state changes are written to main DB + bridge DB simultaneously, then queued as signed webhooks to <code>app.kgob.com</code>.
            Use these controls when app.kgob.com has been offline or when bridge connectivity briefly dropped.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline" size="sm"
              onClick={drainOutbox} disabled={draining}
              data-testid="subs-drain-outbox"
            >
              <ArrowUpRight className={`w-4 h-4 mr-2 ${draining ? 'animate-pulse' : ''}`} />
              Force-Drain Outbox &amp; Bridge
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={retryFailed}
              disabled={!stats?.outbox?.failed}
              data-testid="subs-retry-failed"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Re-queue Failed ({stats?.outbox?.failed || 0})
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <Select data-testid="subscriptions-tab-select-1" value={exportListFilter} onValueChange={setExportListFilter}>
                <SelectTrigger data-testid="subscriptions-tab-selecttrigger-3" className="w-44">
                  <SelectValue placeholder="All lists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="subscriptions-tab-selectitem-7" value="all">All lists</SelectItem>
                  {(stats?.per_list || []).map(l => (
                    <SelectItem data-testid="subscriptions-tab-selectitem-6" key={l.list_id} value={l.list_id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="subs-csv-export">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RECENT ACTIVITY */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-base font-bold text-[#0B2240] mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#D4AF37]" />
            Recent Activity ({recent.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">List</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Last Change</th>
                  <th className="py-2 pr-3">Bridge</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center text-gray-400">No subscriptions yet.</td></tr>
                )}
                {recent.map(row => (
                  <tr key={row.subscription_id} className="border-b border-gray-100" data-testid={`recent-${row.subscription_id}`}>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {row.email}
                      {row.first_name && <div className="text-[10px] text-gray-500">{row.first_name}</div>}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge style={{ backgroundColor: (LIST_LABELS[row.list_id]?.color || '#666') + '22', color: LIST_LABELS[row.list_id]?.color || '#666' }} className="text-xs">
                        {LIST_LABELS[row.list_id]?.label || row.list_id}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-600">{row.source || '—'}</td>
                    <td className="py-2 pr-3 text-xs text-gray-600">{fmtDate(row.updated_at)}</td>
                    <td className="py-2 pr-3">
                      {row.bridge_synced ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" title="Mirrored to bridge DB" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-amber-500" title="Not yet mirrored" />
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => suppressEmail(row.email)}
                        className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                        data-testid={`recent-suppress-${row.subscription_id}`}
                      >
                        Suppress
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* SEARCH */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-base font-bold text-[#0B2240] mb-4">Search Subscribers</h3>
          <form data-testid="subscriptions-tab-form-1" onSubmit={runSearch} className="flex flex-wrap gap-2 mb-4">
            <Input
              placeholder="Email substring (e.g. @example.com)"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 min-w-[240px]"
              data-testid="subs-search-input"
            />
            <Select data-testid="subscriptions-tab-select-2" value={searchStatus} onValueChange={setSearchStatus}>
              <SelectTrigger data-testid="subscriptions-tab-selecttrigger-2" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="subscriptions-tab-selectitem-5" value="all">Any status</SelectItem>
                <SelectItem data-testid="subscriptions-tab-selectitem-4" value="active">Active</SelectItem>
                <SelectItem data-testid="subscriptions-tab-selectitem-3" value="suppressed">Suppressed</SelectItem>
              </SelectContent>
            </Select>
            <Select data-testid="subscriptions-tab-select-1" value={searchList} onValueChange={setSearchList}>
              <SelectTrigger data-testid="subscriptions-tab-selecttrigger-1" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="subscriptions-tab-selectitem-2" value="all">Any list</SelectItem>
                {(stats?.per_list || []).map(l => (
                  <SelectItem data-testid="subscriptions-tab-selectitem-1" key={l.list_id} value={l.list_id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="subs-search-submit">Search</Button>
          </form>
          {searchRows.length > 0 && (
            <div className="space-y-3">
              {searchRows.map(row => (
                <div key={row.subscriber_id} className="border border-gray-200 rounded-md p-3" data-testid={`search-row-${row.subscriber_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-mono text-sm text-[#0B2240]">{row.email}</div>
                      {row.first_name && <div className="text-xs text-gray-500">{row.first_name} {row.last_name || ''}</div>}
                      <div className="text-[10px] text-gray-400 mt-1">
                        First seen via <strong>{row.first_source || 'unknown'}</strong> · {fmtDate(row.created_at)}
                        {row.consent_geo?.country && ` · ${row.consent_geo.city || ''} ${row.consent_geo.region || ''} ${row.consent_geo.country}`}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={row.status} />
                      {!row.bridge_synced && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Not mirrored</Badge>}
                    </div>
                  </div>
                  {row.subscriptions?.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {(row.subscriptions || []).map(s => (
                        <div key={s.list_id} className="bg-gray-50 px-2 py-1.5 rounded text-xs">
                          <div className="font-semibold">{LIST_LABELS[s.list_id]?.label || s.list_id}</div>
                          <div className="text-gray-600">{s.status}{s.source ? ` · ${s.source}` : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {searchRows.length === 0 && searchQuery && (
            <div className="text-center text-gray-400 text-sm py-6">No matches.</div>
          )}
        </CardContent>
      </Card>

      {/* SUPPRESSION LIST */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-base font-bold text-[#0B2240] mb-4 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-600" />
            Suppression List ({suppressions.length})
          </h3>
          {suppressions.length === 0 ? (
            <p className="text-sm text-gray-500">No suppressed addresses.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Reason</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {suppressions.map(row => (
                    <tr key={row.email} className="border-b border-gray-100">
                      <td className="py-2 pr-3 font-mono text-xs">{row.email}</td>
                      <td className="py-2 pr-3"><Badge variant="destructive" className="text-xs">{row.reason}</Badge></td>
                      <td className="py-2 pr-3 text-xs text-gray-600">{row.source || '—'}</td>
                      <td className="py-2 pr-3 text-xs text-gray-600">{fmtDate(row.created_at)}</td>
                      <td className="py-2 pr-3 text-xs text-gray-500 max-w-md truncate">{row.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// -------- helpers --------

function StatCard({ label, value, sublabel, icon, tone = 'navy' }) {
  const toneClass = {
    navy: 'bg-[#0B2240]/5 text-[#0B2240]',
    rust: 'bg-[#B45F3E]/10 text-[#B45F3E]',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-red-100 text-red-700',
  }[tone];
  return (
    <Card>
      <CardContent className="p-5">
        <div className={`inline-flex items-center justify-center w-9 h-9 rounded ${toneClass} mb-3`}>
          {icon}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
        <div className="text-3xl font-bold text-[#0B2240] mt-1">{value}</div>
        <div className="text-xs text-gray-500 mt-1">{sublabel}</div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { className: 'bg-green-100 text-green-800', label: 'Active' },
    pending: { className: 'bg-amber-100 text-amber-800', label: 'Pending' },
    unsubscribed: { className: 'bg-gray-100 text-gray-600', label: 'Unsubscribed' },
    suppressed: { className: 'bg-red-100 text-red-800', label: 'Suppressed' },
  };
  const c = map[status] || { className: 'bg-gray-100 text-gray-600', label: status };
  return <Badge className={`${c.className} text-xs`}>{c.label}</Badge>;
}
